import React, { useEffect, useState } from 'react'
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Button,
  Card,
  CardBody,
  Icon,
  HStack,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Avatar,
  Divider,
} from '@chakra-ui/react'
import { MdBusiness, MdEmail, MdLogout, MdCheckCircle } from 'react-icons/md'
import { useAuth } from '../../contexts/AuthContext'

interface RequireOrganizationProps {
  children: React.ReactNode
}

export const RequireOrganization: React.FC<RequireOrganizationProps> = ({ children }) => {
  const { 
    currentOrg, 
    organizations, 
    isOrgLoading, 
    isSignedIn, 
    loading, 
    isAuthEnabled,
    switchOrganization,
    user,
    signOut
  } = useAuth()
  
  const [showOrgSelector, setShowOrgSelector] = useState(false)
  const [isSelecting, setIsSelecting] = useState(false)
  const [autoSelectAttempted, setAutoSelectAttempted] = useState(false)

  // Auto-select organization if user belongs to exactly one
  useEffect(() => {
    const autoSelectOrg = async () => {
      if (!loading && !isOrgLoading && isSignedIn && !currentOrg && organizations.length === 1 && !autoSelectAttempted) {
        console.log('Auto-selecting the only organization:', organizations[0].name)
        setAutoSelectAttempted(true)
        try {
          await switchOrganization(organizations[0].id)
        } catch (err) {
          console.error('Failed to auto-select organization:', err)
        }
      }
    }
    autoSelectOrg()
  }, [loading, isOrgLoading, isSignedIn, currentOrg, organizations, switchOrganization, autoSelectAttempted])

  // Show selector modal if user belongs to multiple orgs
  useEffect(() => {
    if (!loading && !isOrgLoading && isSignedIn && !currentOrg && organizations.length > 1) {
      setShowOrgSelector(true)
    }
  }, [loading, isOrgLoading, isSignedIn, currentOrg, organizations.length])

  if (!isAuthEnabled) {
    return <>{children}</>
  }

  if (loading || isOrgLoading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minH="100vh"
        bg="gray.50"
      >
        <VStack spacing={4}>
          <Spinner size="xl" color="brand.500" thickness="4px" />
          <Text color="gray.600">Loading...</Text>
        </VStack>
      </Box>
    )
  }

  // Show loading while auto-selecting the only organization
  if (!currentOrg && organizations.length === 1 && !autoSelectAttempted) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minH="100vh"
        bg="gray.50"
      >
        <VStack spacing={4}>
          <Spinner size="xl" color="brand.500" thickness="4px" />
          <Text color="gray.600">Setting up your organization...</Text>
        </VStack>
      </Box>
    )
  }

  if (!isSignedIn) {
    return (
      <Container maxW="container.sm" py={20}>
        <Alert status="warning" borderRadius="lg">
          <AlertIcon />
          <Box>
            <AlertTitle>Not Signed In</AlertTitle>
            <AlertDescription>
              Please sign in to access this page.
            </AlertDescription>
          </Box>
        </Alert>
      </Container>
    )
  }

  const handleSelectOrg = async (orgId: string) => {
    setIsSelecting(true)
    try {
      await switchOrganization(orgId)
      setShowOrgSelector(false)
    } finally {
      setIsSelecting(false)
    }
  }

  const handleSignOut = async () => {
    await signOut()
  }

  if (!currentOrg && organizations.length === 0) {
    return (
      <Box minH="100vh" bg="gray.50" py={20}>
        <Container maxW="container.md">
          <VStack spacing={8}>
            <VStack spacing={4} textAlign="center">
              <Box p={4} bg="orange.50" borderRadius="full">
                <Icon as={MdEmail} boxSize={12} color="orange.500" />
              </Box>
              <Heading size="xl" color="gray.800">No Organization Found</Heading>
              <Text color="gray.600" maxW="lg" fontSize="lg">
                Your email <Text as="span" fontWeight="semibold" color="gray.800">{user?.email}</Text> is not associated with any organization.
              </Text>
            </VStack>

            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg max-w-lg mb-4">
              <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-blue-800">Already received an invitation?</p>
                <p className="text-sm text-blue-700 mt-1">
                  If you've clicked "Accept Invite" from the organization invitation email, please click on that link once again to complete the process.
                </p>
              </div>
            </div>

            <Card w="full" maxW="lg" bg="white" shadow="md">
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <Heading size="sm" color="gray.700">What to do next:</Heading>
                  
                  <HStack align="start" spacing={3}>
                    <Box 
                      w={6} h={6} 
                      borderRadius="full" 
                      bg="brand.100" 
                      color="brand.600"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                      fontSize="sm"
                      fontWeight="bold"
                    >
                      1
                    </Box>
                    <Text color="gray.600">
                      Contact your organization administrator and ask them to add your email address to the organization.
                    </Text>
                  </HStack>

                  <HStack align="start" spacing={3}>
                    <Box 
                      w={6} h={6} 
                      borderRadius="full" 
                      bg="brand.100" 
                      color="brand.600"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                      fontSize="sm"
                      fontWeight="bold"
                    >
                      2
                    </Box>
                    <Text color="gray.600">
                      Once added, sign out and sign back in to access your organization's simulations.
                    </Text>
                  </HStack>

                  <Divider my={2} />

                  <Button
                    variant="outline"
                    colorScheme="gray"
                    leftIcon={<Icon as={MdLogout} />}
                    onClick={handleSignOut}
                    w="full"
                  >
                    Sign Out
                  </Button>
                </VStack>
              </CardBody>
            </Card>

            <Text fontSize="sm" color="gray.500" textAlign="center">
              Need help? Contact your organization administrator or{' '}
              <Text 
                as="a" 
                href="mailto:axoneai.team@gmail.com" 
                color="brand.500" 
                _hover={{ textDecoration: 'underline' }}
              >
                support team
              </Text>.
            </Text>
          </VStack>
        </Container>
      </Box>
    )
  }

  if (organizations.length > 1 && showOrgSelector) {
    return (
      <Modal 
        isOpen={true} 
        onClose={() => {}} 
        closeOnOverlayClick={false}
        closeOnEsc={false}
        isCentered
        size="md"
      >
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent>
          <ModalHeader>
            <VStack spacing={2} align="start">
              <HStack spacing={3}>
                <Box p={2} bg="brand.50" borderRadius="lg">
                  <Icon as={MdBusiness} boxSize={5} color="brand.500" />
                </Box>
                <Text>Select Organization</Text>
              </HStack>
              <Text fontSize="sm" fontWeight="normal" color="gray.500">
                You belong to multiple organizations. Please select one to continue.
              </Text>
            </VStack>
          </ModalHeader>
          <ModalBody pb={6}>
            <VStack spacing={3} align="stretch">
              {organizations.map((org) => (
                <Card
                  key={org.id}
                  cursor={isSelecting ? 'not-allowed' : 'pointer'}
                  onClick={() => !isSelecting && handleSelectOrg(org.id)}
                  _hover={{ borderColor: 'brand.300', shadow: 'md' }}
                  transition="all 0.2s"
                  borderWidth="2px"
                  borderColor="gray.200"
                  opacity={isSelecting ? 0.6 : 1}
                >
                  <CardBody py={4}>
                    <HStack justify="space-between">
                      <HStack spacing={3}>
                        <Avatar
                          size="sm"
                          name={org.name}
                          bg="brand.500"
                          color="white"
                          icon={<Icon as={MdBusiness} />}
                        />
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="medium">{org.name}</Text>
                          <Text fontSize="xs" color="gray.500">
                            {org.membersCount} member{org.membersCount !== 1 ? 's' : ''}
                          </Text>
                        </VStack>
                      </HStack>
                      <Icon as={MdCheckCircle} color="gray.300" boxSize={5} />
                    </HStack>
                  </CardBody>
                </Card>
              ))}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    )
  }

  if (!currentOrg && organizations.length === 1) {
    switchOrganization(organizations[0].id)
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minH="100vh"
        bg="gray.50"
      >
        <VStack spacing={4}>
          <Spinner size="xl" color="brand.500" thickness="4px" />
          <Text color="gray.600">Setting up your organization...</Text>
        </VStack>
      </Box>
    )
  }

  if (!currentOrg) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minH="100vh"
        bg="gray.50"
      >
        <VStack spacing={4}>
          <Spinner size="xl" color="brand.500" thickness="4px" />
          <Text color="gray.600">Loading organization...</Text>
        </VStack>
      </Box>
    )
  }

  return <>{children}</>
}
